export default class DropdownList extends HTMLElement {
    #listElement;
    /** @type {DropdownInputElement[]} */
    #values = [];
    #maxItemWidth = 0;
    #selectedElementIndex = -1;
    #preventScrollFunction;
    #callbacks = {
        onOptionClick: []
    }

    /** @returns {DropdownInputElement[]} */
    get options() {
        return this.#values;
    }

    /** @param {DropdownInputElement[]}values */
    set options(values) {
        this.#setValues(values);
        this.#setHtml();
        this.#setIndexes();
        this.#updatePosition();
        this.#addListeners();
    }

    show() {
        document.addEventListener("wheel", this.#preventScrollFunction, {capture: true, passive: false});
        this.toggleAttribute('open', true)
        this.#updatePosition();
    }

    hide() {
        document.removeEventListener("wheel", this.#preventScrollFunction, {capture: true, passive: false});
        this.toggleAttribute('open', false)
        this.#resetCurrentSelection();
    }

    selectNextItem() {
        const visibleElements = this.#getVisibleElements();
        if (!this.hasSelectedElement()) {
            if (visibleElements.length === 0)
                return;
            this.#setSelectedElement(visibleElements[0].dataset.index);
        } else {
            const indexOf = visibleElements.indexOf(this.#getSelectedElement());
            if (visibleElements.length - 1 <= indexOf) // is last item?
                return;
            this.#setSelectedElement(visibleElements[indexOf + 1].dataset.index);
        }
    }

    selectPrevItem() {
        const visibleElements = this.#getVisibleElements();
        if (!this.hasSelectedElement()) {
            if (visibleElements.length === 0)
                return;
            this.#setSelectedElement(visibleElements[visibleElements.length - 1].dataset.index);
        } else {
            const indexOf = visibleElements.indexOf(this.#getSelectedElement());
            if (indexOf === 0) // is first item?
                return;
            this.#setSelectedElement(visibleElements[indexOf - 1].dataset.index);
        }
    }

    triggerClickOnSelectedItem() {
        if (this.hasSelectedElement())
            this.#getSelectedElement().dispatchEvent(new MouseEvent('mousedown'));
    }

    hasSelectedElement() {
        return this.#getSelectedElement() != null;
    }

    isElementDisabled() {
        return this.#getSelectedElement() && this.#getSelectedElement().hasAttribute('disabled')
    }

    isVisible() {
        return this.hasAttribute('open')
    }

    /** @param {function} cb */
    onOptionClick(cb) {
        this.#callbacks.onOptionClick.push(cb);
    }

    connectedCallback() {
        this.#setHtml();
        this.#addListeners();
        this.#preventScrollFunction = (evt)=> {
            if (!this.contains(evt.target)) {
                // When scrolling outside the list-box
                evt.stopPropagation();
                evt.preventDefault();
            } else {
                const isScrollingUp = evt.deltaY < 0;
                const isScrollingDown = evt.deltaY > 0;
                const isListScrolledToItsTop = this.#listElement.scrollTop === 0;
                const isListScrolledToItsBottom = this.#listElement.scrollTop + this.#listElement.clientHeight >= this.#listElement.scrollHeight;
                const nothingToScrollUp = isScrollingUp & isListScrolledToItsTop;
                const nothingToScrollDown = isScrollingDown & isListScrolledToItsBottom;
                if (nothingToScrollUp || nothingToScrollDown){
                    evt.stopPropagation();
                    evt.preventDefault();
                }
            }
        };
    }

    /** @param {DropdownInputElement[]} values */
    #setValues(values) {
        this.#values = values
    }

    #setIndexes(){
        this.#listElement.querySelectorAll('li').forEach((item, index)=> item.setAttribute(`data-index`, index))
    }


    #getVisibleElements() {
        return [...this.#listElement.querySelectorAll('li:not([hidden])')];
    }

    #addListeners() {
        this.#listElement.addEventListener("mousedown", (evt)=>{
            // When we use DropdownInput in dialog and click on scroll bar, dialog takes focus
            // and list box is closed. So we need to prevent the default behavior of the mousedown event
            evt.preventDefault();
        })
        for (const li of this.querySelectorAll("li")) {
            li.addEventListener('mousedown', (evt) => {
                if (evt.button === 2) return
                const option = this.#values[li.dataset.index];
                this.#callbacks.onOptionClick.forEach(cb => cb(option));
            });
        }
    }

    #setHtml() {
        this.innerHTML = "<ul></ul>"
        this.#listElement = this.querySelector("ul");
        for (const el of this.#values){
            this.#listElement.insertAdjacentHTML('beforeend', el.html);
            if (!el.enabled)
                this.#listElement.lastElementChild.setAttribute('disabled', "true")
        }
    }

    #updatePosition() {
        const parentClientRect = this.parentElement.getBoundingClientRect();
        // if parent element width is greater than max item width, set list width to parent width
        const widthToBe = Math.max(this.#maxItemWidth + 24/*paddings*/ + 22/*scrollbar*/, parentClientRect.width);
        // max allowed width is the distance from the left side of parent element to the right side of the page
        const maxAllowedWidth = document.documentElement.clientWidth - parentClientRect.left
        if (widthToBe > maxAllowedWidth) { // can't fit to the right side, so put it to the left side
            this.#listElement.style.right = 0;
        } else {
            this.#listElement.style.right = null;
        }
        this.#listElement.style.width = `${Math.min(widthToBe, document.documentElement.clientWidth)}px`;
        this.style.width = `${Math.min(widthToBe, document.documentElement.clientWidth)}px`;
        this.style.top = `${parentClientRect.top}px`;
    }

    #getSelectedElement() {
        return this.querySelector(`li[data-index="${this.#selectedElementIndex}"]`);
    }

    /** @param {string} elementIndex */
    #setSelectedElement(elementIndex) {
        this.#resetCurrentSelection();
        this.#selectedElementIndex = elementIndex;
        const element = this.#getSelectedElement();
        element.classList.add('dropdown-list__item--active');
        element.scrollIntoView({block: "nearest"});
    }

    #resetCurrentSelection() {
        if (this.#selectedElementIndex >= 0 && this.#values.length) {
            const selectedElement = this.#getSelectedElement();
            selectedElement.classList.remove('dropdown-list__item--active');
            this.#selectedElementIndex = -1;
        }
    }

    static get is() {
        return 'dropdown-list';
    }
}
